import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Activity, LogOut, Trash2, Inbox, TrendingUp, CheckCircle2, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const STATUS = ["new", "contacted", "won", "lost"];
const statusColor = {
  new: "bg-teal/15 text-teal-muted border-teal/30",
  contacted: "bg-orange/15 text-orange border-orange/30",
  won: "bg-green-500/15 text-green-600 border-green-500/30",
  lost: "bg-slate-200 text-slate-500 border-slate-300",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === false) nav("/admin/login");
    if (user) load();
  }, [user, nav, load]);

  const changeStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${id}`, { status });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (user === null || (user && loading)) {
    return <div className="min-h-screen bg-white grid place-items-center font-mono text-slate-400">Loading…</div>;
  }

  const cards = [
    { label: "Total Leads", value: stats?.total ?? 0, icon: Inbox, color: "text-slate-900" },
    { label: "New", value: stats?.new ?? 0, icon: Clock, color: "text-teal-muted" },
    { label: "Contacted", value: stats?.contacted ?? 0, icon: TrendingUp, color: "text-orange" },
    { label: "Won", value: stats?.won ?? 0, icon: CheckCircle2, color: "text-green-600" },
  ];

  return (
    <div className="min-h-screen bg-[#F4F5F7] text-slate-900">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center w-8 h-8 bg-orange text-white"><Activity className="w-4 h-4" strokeWidth={2.5} /></span>
            <span className="font-display font-extrabold tracking-tight text-slate-900">Lead Dashboard</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-slate-500 hidden sm:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={() => { logout(); nav("/admin/login"); }}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-orange transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-display text-3xl font-black tracking-tight mb-6">Overview</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((c) => (
            <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="bg-white border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{c.label}</span>
                <c.icon className={`w-4 h-4 ${c.color}`} />
              </div>
              <p className="font-display text-4xl font-black">{c.value}</p>
            </div>
          ))}
        </div>

        {stats?.trend?.length > 0 && (
          <div className="bg-white border border-slate-200 p-6 mb-8">
            <h2 className="font-display text-lg font-bold mb-4">Leads over time</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(d) => d?.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #E2E8F0", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Bar dataKey="count" fill="#FF4A1C" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="bg-white border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Leads ({leads.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="leads-table">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-200 bg-slate-50">
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Organization</th>
                  <th className="px-6 py-3 font-medium">Interest</th>
                  <th className="px-6 py-3 font-medium">Date</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400 font-mono text-sm">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors align-top">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">{l.name}</p>
                      <p className="text-xs text-slate-500">{l.email}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{l.role}</p>
                      {l.message && <p className="text-xs text-slate-500 mt-2 max-w-xs">{l.message}</p>}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">{l.organization}</td>
                    <td className="px-6 py-4"><span className="font-mono text-xs bg-slate-100 px-2 py-1">{l.interest}</span></td>
                    <td className="px-6 py-4 text-sm text-slate-500 font-mono">{(l.created_at || "").slice(0, 10)}</td>
                    <td className="px-6 py-4">
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger data-testid={`lead-status-${l.id}`} className={`h-8 w-32 rounded-none border text-xs font-mono uppercase ${statusColor[l.status] || ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {STATUS.map((s) => <SelectItem key={s} value={s} className="text-xs uppercase font-mono">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4">
                      <button data-testid={`lead-delete-${l.id}`} onClick={() => remove(l.id)} className="text-slate-400 hover:text-orange transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
